import { useEffect, useState } from "react";
import type { Task } from "../store/tasks";
import { useTasks } from "../store/tasks";
import { useSettings } from "../store/settings";
import { useClientBrand, effectiveBrand } from "../store/clientBrand";
import { useRH } from "../store/rh";
import { PERSONAS } from "../data/org";
import { profileOf } from "../data/profiles";
import { clientById } from "../data/clients";
import { generateDeckAI, type DeckSpec, type SlideSpec, directArtAI, type ArtDirection,
         criticizeDeckAI, type DeckCritique, type SlideCritique, improveImagePromptAI } from "../lib/claude";
import { PresentationView } from "./PresentationView";
import { slideIllustrationWithDirection } from "../lib/promptTemplates";
import { generateImageGemini } from "../lib/generators";
import { buildPptx, type DeckImages, type DeckAspect } from "../lib/pptxBuilder";
import { resizeForCreative } from "../lib/imageResize";
import { useSocialCreds } from "../store/socialCreds";
import { publishCarousel, publishSingleImage, uploadDataUrl } from "../lib/instagramPublish";
import { getApiBase, getApiToken } from "../lib/cloudSync";
import { useLibrary } from "../store/library";
import { generateIGCaptionAI } from "../lib/claude";
import { CLIENTS } from "../data/clients";
import { useDeckJobs } from "../store/deckJobs";

export function DeckGenerator({ task, onSaved }: { task: Task; onSaved: () => void }) {
  const settings = useSettings();
  const addOutput = useTasks(s => s.addOutput);

  // Restaura state do store global (sobrevive ao fechamento do modal)
  const existingJob = useDeckJobs(s => s.jobs[task.id]);
  const updateJob = useDeckJobs(s => s.update);
  const appendJobLog = useDeckJobs(s => s.appendLog);
  const setJobImages = useDeckJobs(s => s.setImages);
  const patchJobImage = useDeckJobs(s => s.patchImage);

  const [busy, setBusy] = useState(existingJob?.status === "generating" || existingJob?.status === "critiquing" || existingJob?.status === "refining");
  const [log, setLog] = useState<string[]>(existingJob?.log ?? []);
  const [deck, setDeck] = useState<DeckSpec | null>(existingJob?.deck ?? null);
  const [images, setImages] = useState<DeckImages>(existingJob?.images ?? {});

  // sincroniza store ↔ state
  useEffect(() => {
    if (existingJob) {
      setLog(existingJob.log);
      setDeck(existingJob.deck);
      setImages(existingJob.images);
      if (existingJob.critique) setCritique(existingJob.critique);
      if (existingJob.artDirection) setArtDirection(existingJob.artDirection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingJob?.updatedAt]);
  const [numSlides, setNumSlides] = useState(8);
  const [agentId, setAgentId] = useState<string>(task.assignedTo[0] ?? "");
  const [withImages, setWithImages] = useState(true);
  const [aspect, setAspect] = useState<DeckAspect>("4:5");  // padrão Instagram
  const [presenting, setPresenting] = useState(false);
  const [artDirection, setArtDirection] = useState<ArtDirection | null>(null);
  const [useArtDirection, setUseArtDirection] = useState(true);
  const [critique, setCritique] = useState<DeckCritique | null>(null);
  const [autoRefine, setAutoRefine] = useState(true);
  const [usePromptMaster, setUsePromptMaster] = useState(true);
  const [showPublish, setShowPublish] = useState(false);
  const socialAccounts = useSocialCreds(s =>
    task.clientId ? s.accounts[task.clientId] ?? [] : []
  );

  const append = (s: string) => {
    setLog(l => [...l, s]);
    appendJobLog(task.id, s);
  };

  // helper: atualiza imagens locais + job global
  const setImagesSynced = (next: DeckImages | ((prev: DeckImages) => DeckImages)) => {
    setImages(prev => {
      const out = typeof next === "function" ? (next as (p: DeckImages) => DeckImages)(prev) : next;
      setJobImages(task.id, out);
      return out;
    });
  };

  // Refaz UM slide individual (com fix opcional). Usado pelos botões da crítica.
  const regenerateSingleSlide = async (idx: number, fixHint?: string) => {
    if (!deck) return;
    if (!settings.geminiKey) { alert("Configure chave Gemini em ⚙️."); return; }
    const slide = deck.slides[idx];
    if (!slide?.imagePrompt) { alert("Esse slide não tem imagePrompt."); return; }

    append(`↺ refazendo slide ${idx + 1}${fixHint ? ` com fix: "${fixHint.slice(0, 60)}"` : ""}…`);
    try {
      const cli = task.clientId ? clientById(task.clientId) : undefined;
      const ov  = task.clientId ? useClientBrand.getState().overrides[task.clientId] : undefined;
      const eff = cli ? effectiveBrand(cli, ov) : undefined;
      const clientContext = cli && eff
        ? `${cli.name} (${cli.segment}). Pitch: ${eff.pitch}. Público: ${eff.audience}.`
        : undefined;

      const slide1Based = idx + 1;
      const logoOnThis = !!artDirection?.logoUsage.include && artDirection.logoUsage.slides.includes(slide1Based);
      const spokesOnThis = !!artDirection?.spokespersonUsage.include && artDirection.spokespersonUsage.slides.includes(slide1Based);

      let finalImagePrompt = artDirection
        ? slideIllustrationWithDirection({
            slideIndex: slide1Based, slideTitle: slide.title,
            concept: slide.imagePrompt + (fixHint ? ` IMPORTANT FIX: ${fixHint}` : ""),
            layout: (slide.layout ?? "bullets") as any,
            brand: eff ? { primary: eff.primary, secondary: eff.secondary, accent: eff.accent } : {},
            art: {
              photoStyle: artDirection.photoStyle,
              paletteIntensity: artDirection.paletteIntensity,
              aesthetic: artDirection.aesthetic,
              mood: artDirection.mood,
              avoid: artDirection.avoid,
              logoOnThisSlide: logoOnThis,
              logoPlacement: artDirection.logoUsage.placement,
              logoOpacity: artDirection.logoUsage.opacity,
              spokespersonOnThisSlide: spokesOnThis,
              spokespersonAngle: artDirection.spokespersonUsage.angle,
              spokespersonMood: artDirection.spokespersonUsage.mood
            }
          })
        : `${slide.imagePrompt}${fixHint ? ` IMPORTANT: ${fixHint}` : ""}`;

      // Ruben otimiza se ligado
      if (usePromptMaster && settings.anthropicKey) {
        try {
          const better = await improveImagePromptAI({
            apiKey: settings.anthropicKey, rawPrompt: finalImagePrompt,
            format: `slide ${slide.layout} ${aspect}`, aspect,
            brandPalette: eff ? { primary: eff.primary, secondary: eff.secondary, accent: eff.accent } : undefined,
            clientContext, spokespersonRef: spokesOnThis, specialist: "ruben"
          });
          if (better && better.length > 50) finalImagePrompt = better;
        } catch { /* fallback */ }
      }

      const referenceUrl =
        spokesOnThis && eff?.spokespersonUrl ? eff.spokespersonUrl
        : logoOnThis && eff?.logoUrl ? eff.logoUrl
        : undefined;
      const imgAspect: "16:9" | "9:16" | "1:1" | "4:5" =
        aspect === "16:9" ? "16:9" : aspect === "9:16" ? "9:16" : aspect === "1:1" ? "1:1" : "4:5";

      const im = await generateImageGemini({
        apiKey: settings.geminiKey, model: settings.geminiImageModel,
        prompt: finalImagePrompt, aspect: imgAspect, referenceUrl
      });
      let final = im.dataUrl;
      try { final = await resizeForCreative(im.dataUrl); } catch {}

      setImagesSynced(prev => ({ ...prev, [idx]: final }));
      append(`  ✓ slide ${idx + 1} regerado`);
    } catch (e) {
      append(`  ✗ slide ${idx + 1} falhou: ${(e as Error).message.slice(0, 100)}`);
    }
  };

  const regenerateAllFlagged = async () => {
    if (!critique) return;
    const toFix = critique.slides.filter(s => s.decision === "regenerate");
    if (toFix.length === 0) { alert("Nenhum slide marcado pra refazer."); return; }
    setBusy(true);
    updateJob(task.id, { status: "refining" });
    for (const s of toFix) {
      await regenerateSingleSlide(s.slideIndex - 1, s.fixHint);
    }
    setBusy(false);
    updateJob(task.id, { status: "done" });
  };

  const generate = async () => {
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    const agent = PERSONAS.find(p => p.id === agentId) ?? PERSONAS.find(p => p.id === "marcio")!;
    setBusy(true);
    const initialLog = [`▸ ${agent.name} estruturando ${numSlides} slides…`];
    setLog(initialLog);
    // Inicia job global (sobrevive ao close do modal)
    updateJob(task.id, {
      status: "generating", agentId, aspect, log: initialLog,
      deck: null, images: {}, critique: null, artDirection: null,
      startedAt: Date.now()
    });
    try {
      const prof = profileOf(agent.id, agent.tier);
      const cli = task.clientId ? clientById(task.clientId) : undefined;
      const ov  = task.clientId ? useClientBrand.getState().overrides[task.clientId] : undefined;
      const eff = cli ? effectiveBrand(cli, ov) : undefined;
      const clientContext = cli && eff
        ? `${cli.name} (${cli.segment}). Pitch: ${eff.pitch}. Público: ${eff.audience}.` +
          (eff.persona ? ` Persona: ${eff.persona}.` : "") +
          (eff.voiceTone ? ` Tom: ${eff.voiceTone}.` : "") +
          ` Paleta: primário ${eff.primary}, secundário ${eff.secondary}, acento ${eff.accent}.`
        : undefined;

      const aspectHint = aspect === "4:5" || aspect === "9:16"
        ? `IMPORTANTE: o formato é ${aspect} VERTICAL (carrossel Instagram/Stories). ` +
          `Title curto (max 6 palavras), bullets de no máximo 8 palavras cada, no máximo 3 bullets por slide. ` +
          `Layouts ideais: cover, section, image-right (vira stacked vertical), metric, quote, bullets, closing. ` +
          `EVITE muito texto — design mobile-first.`
        : aspect === "1:1"
        ? `Formato 1:1 square. Title médio, bullets curtos (max 10 palavras), no máximo 4 bullets por slide.`
        : `Formato 16:9 widescreen tradicional. Layout livre.`;

      const out = await generateDeckAI({
        apiKey: settings.anthropicKey, model: settings.model,
        taskTitle: task.title,
        taskDescription: task.description + "\n\n" + aspectHint,
        agentName: agent.name, agentVoice: prof.voice,
        clientContext, numSlides
      });

      // ============ DIREÇÃO DE ARTE ============
      let art: ArtDirection | null = null;
      if (useArtDirection) {
        append("🎨 Paula Scher decidindo direção de arte…");
        try {
          art = await directArtAI({
            apiKey: settings.anthropicKey, model: settings.model,
            taskTitle: task.title, taskDescription: task.description,
            hasLogo: !!eff?.logoUrl,
            hasSpokesperson: !!eff?.spokespersonUrl,
            clientContext, numSlides: out.slides.length
          });
          setArtDirection(art);
          updateJob(task.id, { artDirection: art });
          append(`✓ direção: ${art.aesthetic} · ${art.mood} · paleta ${art.paletteIntensity}`);
          append(`  logo: ${art.logoUsage.include ? `slides [${art.logoUsage.slides.join(",")}] · ${art.logoUsage.placement}` : "não usar"}`);
          append(`  spokesperson: ${art.spokespersonUsage.include ? `slides [${art.spokespersonUsage.slides.join(",")}]` : "não usar"}`);
        } catch (e) {
          append("⚠ direção de arte falhou: " + (e as Error).message.slice(0, 80));
        }
      }
      // injeta cores do brand kit no deck
      if (eff) {
        out.brandPrimary   = eff.primary;
        out.brandSecondary = eff.secondary;
        out.brandAccent    = eff.accent;
      }
      setDeck(out);
      updateJob(task.id, { deck: out });
      append(`✓ deck pronto: ${out.slides.length} slides`);

      // helper que gera a imagem de UM slide (reutilizado em geração inicial + refinamento)
      const generateImageForSlide = async (idx: number, slide: SlideSpec, prompt: string, extraFixHint?: string): Promise<string | null> => {
        if (!settings.geminiKey) return null;
        const slide1Based = idx + 1;
        const logoOnThis = !!art?.logoUsage.include && art.logoUsage.slides.includes(slide1Based);
        const spokesOnThis = !!art?.spokespersonUsage.include && art.spokespersonUsage.slides.includes(slide1Based);
        let finalImagePrompt = art
          ? slideIllustrationWithDirection({
              slideIndex: slide1Based, slideTitle: slide.title,
              concept: prompt + (extraFixHint ? ` IMPORTANT FIX: ${extraFixHint}` : ""),
              layout: (slide.layout ?? "bullets") as any,
              brand: eff ? { primary: eff.primary, secondary: eff.secondary, accent: eff.accent } : {},
              art: {
                photoStyle: art.photoStyle, paletteIntensity: art.paletteIntensity,
                aesthetic: art.aesthetic, mood: art.mood, avoid: art.avoid,
                logoOnThisSlide: logoOnThis, logoPlacement: art.logoUsage.placement, logoOpacity: art.logoUsage.opacity,
                spokespersonOnThisSlide: spokesOnThis,
                spokespersonAngle: art.spokespersonUsage.angle,
                spokespersonMood: art.spokespersonUsage.mood
              }
            })
          : `${prompt}${extraFixHint ? ` IMPORTANT: ${extraFixHint}` : ""}`;

        // ⭐ PROMPT MASTER: Ruben Hassid otimiza o prompt antes do Gemini
        if (usePromptMaster && settings.anthropicKey) {
          try {
            const better = await improveImagePromptAI({
              apiKey: settings.anthropicKey,
              rawPrompt: finalImagePrompt,
              format: `slide ${slide.layout} ${aspect}`,
              aspect,
              brandPalette: eff ? { primary: eff.primary, secondary: eff.secondary, accent: eff.accent } : undefined,
              clientContext: clientContext,
              spokespersonRef: spokesOnThis,
              specialist: "ruben"
            });
            if (better && better.length > 50) finalImagePrompt = better;
          } catch { /* fallback pro original */ }
        }
        const referenceUrl =
          spokesOnThis && eff?.spokespersonUrl ? eff.spokespersonUrl
          : logoOnThis && eff?.logoUrl ? eff.logoUrl
          : undefined;
        const imgAspect: "16:9" | "9:16" | "1:1" | "4:5" =
          aspect === "16:9" ? "16:9"
          : aspect === "9:16" ? "9:16"
          : aspect === "1:1" ? "1:1"
          : "4:5";
        const im = await generateImageGemini({
          apiKey: settings.geminiKey, model: settings.geminiImageModel,
          prompt: finalImagePrompt, aspect: imgAspect, referenceUrl
        });
        let final = im.dataUrl;
        try { final = await resizeForCreative(im.dataUrl); } catch {}
        return final;
      };

      if (withImages) {
        const needs = out.slides
          .map((s, i) => ({ idx: i, prompt: s.imagePrompt }))
          .filter(x => x.prompt);
        append(`▸ gerando ${needs.length} imagens via Gemini…`);
        const imgs: DeckImages = {};
        for (const { idx, prompt } of needs) {
          if (!settings.geminiKey) {
            append(`  ✗ pulando slide ${idx+1}: sem chave Gemini`);
            continue;
          }
          try {
            const slide = out.slides[idx];
            const slide1Based = idx + 1;
            const logoOnThis = !!art?.logoUsage.include && art.logoUsage.slides.includes(slide1Based);
            const spokesOnThis = !!art?.spokespersonUsage.include && art.spokespersonUsage.slides.includes(slide1Based);

            // monta prompt com direção de arte (se houver) ou fallback
            const finalImagePrompt = art
              ? slideIllustrationWithDirection({
                  slideIndex: slide1Based,
                  slideTitle: slide.title,
                  concept: prompt!,
                  layout: (slide.layout ?? "bullets") as any,
                  brand: eff
                    ? { primary: eff.primary, secondary: eff.secondary, accent: eff.accent }
                    : {},
                  art: {
                    photoStyle: art.photoStyle,
                    paletteIntensity: art.paletteIntensity,
                    aesthetic: art.aesthetic,
                    mood: art.mood,
                    avoid: art.avoid,
                    logoOnThisSlide: logoOnThis,
                    logoPlacement: art.logoUsage.placement,
                    logoOpacity: art.logoUsage.opacity,
                    spokespersonOnThisSlide: spokesOnThis,
                    spokespersonAngle: art.spokespersonUsage.angle,
                    spokespersonMood: art.spokespersonUsage.mood
                  }
                })
              : `${prompt} Paleta sutil ${eff?.primary ?? ""}. Foto editorial premium. Sem texto na imagem.`;

            // escolhe referência: spokesperson > logo > nenhuma
            const referenceUrl =
              spokesOnThis && eff?.spokespersonUrl ? eff.spokespersonUrl
              : logoOnThis && eff?.logoUrl ? eff.logoUrl
              : undefined;

            const imgAspect: "16:9" | "9:16" | "1:1" | "4:5" =
              aspect === "16:9" ? "16:9"
              : aspect === "9:16" ? "9:16"
              : aspect === "1:1" ? "1:1"
              : "4:5";
            const im = await generateImageGemini({
              apiKey: settings.geminiKey,
              model: settings.geminiImageModel,
              prompt: finalImagePrompt,
              aspect: imgAspect,
              referenceUrl
            });
            let final = im.dataUrl;
            try { final = await resizeForCreative(im.dataUrl); } catch {}
            imgs[idx] = final;
            append(`  ✓ slide ${idx+1} ilustrado`);
            setImages(prev => ({ ...prev, [idx]: final }));
            patchJobImage(task.id, idx, final);
          } catch (e) {
            append(`  ✗ slide ${idx+1} falhou: ${(e as Error).message.slice(0,80)}`);
            imgs[idx] = null;
          }
        }
        setImages(imgs);
        setJobImages(task.id, imgs);
      }

      // ============ CRÍTICA DA PAULA SCHER ============
      updateJob(task.id, { status: "critiquing" });
      try {
        append("🔍 Paula Scher critique mode ON — revisando deck…");
        const hasImages = out.slides.map((_, i) => !!images[i] || (withImages && !!out.slides[i].imagePrompt));
        const crit = await criticizeDeckAI({
          apiKey: settings.anthropicKey, model: settings.model,
          deck: out, hasImages, clientContext
        });
        setCritique(crit);
        updateJob(task.id, { critique: crit });
        append(`  📊 nota geral: ${crit.overallScore}/10 · monotonia: ${crit.monotony}/10`);
        if (crit.paletteIssue) append(`  ⚠ paleta: ${crit.paletteIssue}`);
        if (crit.layoutIssue) append(`  ⚠ layout: ${crit.layoutIssue}`);
        const toFix = crit.slides.filter(s => s.decision === "regenerate");
        const toKill = crit.slides.filter(s => s.decision === "kill");
        append(`  ✓ aprovados: ${crit.slides.filter(s => s.decision === "approve").length}`);
        append(`  ↺ refazer: ${toFix.length}`);
        append(`  ✗ matar: ${toKill.length}`);

        // ============ AUTO-REFINAMENTO ============
        if (autoRefine && (toFix.length > 0 || toKill.length > 0)) {
          updateJob(task.id, { status: "refining" });
          append("🔧 auto-refinando slides reprovados…");

          // 1) Remove slides "kill"
          let workingSlides = [...out.slides];
          const killIdx = new Set(toKill.map(s => s.slideIndex - 1));
          if (killIdx.size > 0) {
            workingSlides = workingSlides.filter((_, i) => !killIdx.has(i));
            append(`  ✗ ${killIdx.size} slide(s) removido(s)`);
          }

          // 2) Refaz "regenerate" — só as imagens com fixHint da Paula
          //    (regerar o conteúdo de texto exigiria 2ª chamada Claude por slide; deixa as imagens novas resolverem)
          const newImages: DeckImages = { ...images };
          // index shift: precisamos mapear slideIndex original → novo idx
          const oldToNew = new Map<number, number>();
          let newIdx = 0;
          for (let oldIdx = 0; oldIdx < out.slides.length; oldIdx++) {
            if (killIdx.has(oldIdx)) continue;
            oldToNew.set(oldIdx, newIdx);
            newIdx++;
          }
          // rebuild newImages com new indexes
          const rebuiltImages: DeckImages = {};
          for (const [oldIdx, newI] of oldToNew.entries()) {
            if (images[oldIdx]) rebuiltImages[newI] = images[oldIdx];
          }

          for (const fixItem of toFix) {
            const origIdx = fixItem.slideIndex - 1;
            if (killIdx.has(origIdx)) continue;
            const newI = oldToNew.get(origIdx);
            if (newI === undefined) continue;
            const slide = workingSlides[newI];
            if (!slide?.imagePrompt) continue;
            append(`  ↺ slide ${newI + 1}: ${fixItem.critique.slice(0, 80)}`);
            try {
              const newImg = await generateImageForSlide(newI, slide, slide.imagePrompt, fixItem.fixHint);
              if (newImg) rebuiltImages[newI] = newImg;
              append(`    ✓ regerado`);
              setImages({ ...rebuiltImages });
            } catch (e) {
              append(`    ✗ falhou: ${(e as Error).message.slice(0, 60)}`);
            }
          }

          // atualiza o deck final
          const finalDeck = { ...out, slides: workingSlides };
          setDeck(finalDeck);
          setImages(rebuiltImages);
          updateJob(task.id, { deck: finalDeck, images: rebuiltImages });
          append(`✓ deck refinado · ${workingSlides.length} slides finais`);
        }

        append(`💬 Paula: "${crit.paulaVerdict}"`);
      } catch (e) {
        append("⚠ crítica falhou: " + (e as Error).message.slice(0, 80));
      }
    } catch (e) {
      append("✗ " + (e as Error).message);
      updateJob(task.id, { status: "error" });
    } finally {
      setBusy(false);
      const cur = useDeckJobs.getState().jobs[task.id];
      if (cur && cur.status !== "error") updateJob(task.id, { status: "done" });
    }
  };

  const downloadPptx = async () => {
    if (!deck) return;
    setBusy(true);
    append("▸ montando .pptx…");
    try {
      const filename = `${task.title.replace(/[^\w\s-]/g, "").slice(0,60)}-${aspect.replace(":","x")}.pptx`;
      await buildPptx(deck, images, filename, { aspect });
      append(`✓ .pptx ${aspect} baixado`);
    } catch (e) {
      append("✗ pptx: " + (e as Error).message);
    } finally { setBusy(false); }
  };

  const saveAsOutput = () => {
    if (!deck) return;
    const agent = PERSONAS.find(p => p.id === agentId) ?? PERSONAS.find(p => p.id === "marcio")!;
    // serializa o deck como markdown legível pra ficar bonito no card
    const md =
`# ${deck.title}\n${deck.subtitle ? `_${deck.subtitle}_\n` : ""}\n` +
deck.slides.map((s, i) => slideToMarkdown(s, i)).join("\n\n---\n\n");
    addOutput(task.id, agent.id, md, {
      kind: "text",
      provider: "anthropic+gemini",
      model: `deck/${deck.slides.length}slides`
    });

    // adiciona à biblioteca
    const libId = useLibrary.getState().add({
      clientId: task.clientId,
      taskId: task.id,
      taskTitle: task.title,
      presenterId: agent.id,
      aspect,
      deck,
      images
    });

    // avança o estágio do pipeline
    const setStageFn = useTasks.getState().setStage;
    const current = task.stage ?? "draft";
    const nextStage =
      current === "draft" || current === "delegated" ? "supervisor-review"
      : current === "in-progress" ? "supervisor-review"
      : current === "supervisor-review" ? "client-approval"
      : current === "client-approval" ? "delivered"
      : current;
    setStageFn(task.id, nextStage);

    alert(
      `✓ Entrega salva!\n\n` +
      `→ Adicionada na Biblioteca (ID: ${libId.slice(0,8)})\n` +
      `→ Demanda avançou: ${current} → ${nextStage}\n\n` +
      (nextStage === "supervisor-review" ? "Próximo: supervisor revisa." :
       nextStage === "client-approval" ? "Próximo: Marcio aprova." :
       "Status: ENTREGUE.")
    );
    onSaved();
  };

  return (
    <div className="border-2 border-ink p-4 bg-bone">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display font-bold uppercase text-base">📊 gerar apresentação visual</div>
          <div className="font-crimson italic text-paper text-sm">
            Claude estrutura slides → Gemini ilustra → você baixa .pptx ou salva como entrega
          </div>
        </div>
      </div>

      <div className="mb-3">
        <div className="tag text-graydk mb-1">formato (tamanho do slide)</div>
        <div className="flex flex-wrap gap-1.5">
          {([
            { v: "4:5" , l: "4:5 · Carrossel Instagram", hint: "1080×1350 — feed nativo" },
            { v: "1:1" , l: "1:1 · Square",              hint: "1080×1080 — square" },
            { v: "9:16", l: "9:16 · Story / Reel",       hint: "1080×1920 — vertical" },
            { v: "16:9", l: "16:9 · Apresentação",       hint: "widescreen — PowerPoint" }
          ] as { v: DeckAspect; l: string; hint: string }[]).map(opt => (
            <button key={opt.v} onClick={() => setAspect(opt.v)}
              title={opt.hint}
              className={`btn ${aspect === opt.v ? "bg-red text-bone border-red" : "btn-fire"}`}>
              {opt.l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-2 mb-3">
        <div>
          <div className="tag text-graydk mb-1">na voz de</div>
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)}
            className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
            {(task.assignedTo.length > 0 ? task.assignedTo : PERSONAS.map(p => p.id))
              .map(id => PERSONAS.find(p => p.id === id)).filter(Boolean)
              .map(p => <option key={p!.id} value={p!.id}>{p!.name}</option>)}
          </select>
        </div>
        <div>
          <div className="tag text-graydk mb-1">nº de slides</div>
          <input type="number" min={4} max={20} value={numSlides}
                 onChange={(e) => setNumSlides(Number(e.target.value))}
                 className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]" />
        </div>
        <div>
          <div className="tag text-graydk mb-1">ilustrações</div>
          <label className="flex items-center gap-2 text-[12px] font-mono mt-1.5">
            <input type="checkbox" checked={withImages}
                   onChange={(e) => setWithImages(e.target.checked)} />
            gerar imagens via Gemini
          </label>
          <label className="flex items-center gap-2 text-[12px] font-mono mt-1">
            <input type="checkbox" checked={useArtDirection}
                   onChange={(e) => setUseArtDirection(e.target.checked)} />
            🎨 direção de arte (Paula Scher)
          </label>
          <label className="flex items-center gap-2 text-[12px] font-mono mt-1">
            <input type="checkbox" checked={autoRefine}
                   onChange={(e) => setAutoRefine(e.target.checked)} />
            🔍 Paula critica + refaz slides ruins
          </label>
          <label className="flex items-center gap-2 text-[12px] font-mono mt-1">
            <input type="checkbox" checked={usePromptMaster}
                   onChange={(e) => setUsePromptMaster(e.target.checked)} />
            ⭐ Ruben Hassid otimiza prompts de imagem
          </label>
        </div>
      </div>

      {/* PAINEL DA DIREÇÃO DE ARTE */}
      {artDirection && (
        <section className="border-2 border-red bg-red/5 p-3 mb-3">
          <div className="font-display font-bold uppercase text-sm mb-2 text-red">
            🎨 Direção de arte · Paula Scher
          </div>
          <div className="grid md:grid-cols-2 gap-2 text-[12px]">
            <div>
              <div className="tag text-graydk">estilo fotográfico</div>
              <p className="font-mono">{artDirection.photoStyle}</p>
            </div>
            <div>
              <div className="tag text-graydk">estética · mood</div>
              <p className="font-mono">{artDirection.aesthetic} · <span className="text-red font-bold">{artDirection.mood}</span></p>
            </div>
            <div>
              <div className="tag text-graydk">logo</div>
              <p className="font-mono">
                {artDirection.logoUsage.include
                  ? `slides [${artDirection.logoUsage.slides.join(", ")}] · ${artDirection.logoUsage.placement} · ${Math.round(artDirection.logoUsage.opacity * 100)}%`
                  : "não usar"}
              </p>
            </div>
            <div>
              <div className="tag text-graydk">spokesperson</div>
              <p className="font-mono">
                {artDirection.spokespersonUsage.include
                  ? `slides [${artDirection.spokespersonUsage.slides.join(", ")}] · ${artDirection.spokespersonUsage.angle}`
                  : "não usar"}
              </p>
            </div>
            <div>
              <div className="tag text-graydk">paleta</div>
              <p className="font-mono">{artDirection.paletteIntensity}</p>
            </div>
            <div>
              <div className="tag text-graydk">evitar</div>
              <p className="font-mono">{artDirection.avoid.join(" · ")}</p>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-red/30">
            <div className="tag text-graydk">justificativa</div>
            <p className="font-crimson italic text-paper text-sm">"{artDirection.rationale}"</p>
          </div>
        </section>
      )}

      <div className="flex gap-2 mb-3 flex-wrap">
        <button onClick={generate} disabled={busy}
          className="btn bg-ink text-bone border-ink disabled:opacity-50">
          {busy ? "gerando…" : "✦ gerar deck"}
        </button>
        {deck && (
          <>
            <button onClick={() => setPresenting(true)}
              className="btn bg-red text-bone border-red text-base py-2.5 px-4">
              ▶ APRESENTAR AO VIVO
            </button>
            <button onClick={downloadPptx} disabled={busy}
              className="btn bg-ink text-bone border-ink disabled:opacity-50">
              ↓ baixar .pptx
            </button>
            <button onClick={saveAsOutput} className="btn bg-gold text-ink border-gold">
              ↳ salvar como entrega
            </button>
            {socialAccounts.some(a => a.platform === "instagram") && (
              <button onClick={() => setShowPublish(true)}
                className="btn"
                style={{ background: "#E1306C", color: "white", borderColor: "#E1306C" }}>
                📤 publicar no Instagram
              </button>
            )}
          </>
        )}
      </div>

      {log.length > 0 && (
        <pre className="bg-ink/95 text-bone font-mono text-[11px] p-2 mb-3 max-h-32 overflow-y-auto whitespace-pre-wrap">
{log.join("\n")}
        </pre>
      )}

      {critique && (
        <section className="border-2 border-ink bg-red/5 mb-3">
          <header className="bg-red text-bone px-3 py-2 flex items-center justify-between flex-wrap gap-2">
            <div>
              <span className="font-display font-bold uppercase">🔍 CRÍTICA DA PAULA SCHER</span>
              <span className="ml-2 tag bg-bone text-ink px-1.5">
                nota geral {critique.overallScore}/10
              </span>
              <span className="ml-1 tag bg-bone text-ink px-1.5">
                monotonia {critique.monotony}/10
              </span>
            </div>
            <div className="flex items-center gap-1">
              {critique.slides.some(s => s.decision === "regenerate") && (
                <button onClick={regenerateAllFlagged} disabled={busy}
                  className="btn bg-bone text-ink border-bone disabled:opacity-50">
                  ↺ Paula refaz todos os marcados ({critique.slides.filter(s => s.decision === "regenerate").length})
                </button>
              )}
              <button onClick={() => setCritique(null)} className="tag bg-bone text-ink px-2">×</button>
            </div>
          </header>
          <div className="p-3">
            <p className="font-crimson italic text-paper text-[14px] mb-3 border-l-4 border-red pl-3">
              "{critique.paulaVerdict}"
              <span className="block text-[11px] mt-1 not-italic font-mono opacity-70">— Paula Scher, diretora de arte</span>
            </p>
            {(critique.paletteIssue || critique.layoutIssue) && (
              <div className="grid md:grid-cols-2 gap-2 mb-3 text-[12px]">
                {critique.paletteIssue && (
                  <div className="border border-red/30 bg-bone p-2">
                    <div className="tag text-red">paleta · problema</div>
                    <p className="font-mono">{critique.paletteIssue}</p>
                  </div>
                )}
                {critique.layoutIssue && (
                  <div className="border border-red/30 bg-bone p-2">
                    <div className="tag text-red">layout · problema</div>
                    <p className="font-mono">{critique.layoutIssue}</p>
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
              {critique.slides.map(s => (
                <div key={s.slideIndex}
                     className={`border-2 p-2 text-[11px] font-mono
                       ${s.decision === "approve" ? "border-ink bg-bone"
                         : s.decision === "regenerate" ? "border-gold bg-gold/10"
                         : "border-red bg-red/10"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-display font-bold uppercase">#{String(s.slideIndex).padStart(2,"0")}</span>
                    <span className={`tag px-1
                      ${s.score >= 7 ? "bg-ink text-bone"
                        : s.score >= 4 ? "bg-gold text-ink"
                        : "bg-red text-bone"}`}>
                      {s.score}/10
                    </span>
                  </div>
                  <div className="tag mb-1">
                    {s.decision === "approve" ? "✓ APROVADO" : s.decision === "regenerate" ? "↺ REFAZER" : "✗ MATAR"}
                  </div>
                  <p className="leading-tight">{s.critique}</p>
                  {s.fixHint && (
                    <p className="mt-1 text-gold italic">fix: {s.fixHint}</p>
                  )}
                  {s.decision !== "approve" && (
                    <button
                      onClick={() => regenerateSingleSlide(s.slideIndex - 1, s.fixHint)}
                      disabled={busy}
                      className="btn bg-red text-bone border-red mt-1 py-0.5 text-[10px] disabled:opacity-50">
                      ↺ Paula refaz este slide
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {deck && <DeckPreview deck={deck} images={images} aspect={aspect} />}

      {presenting && deck && (
        <PresentationView
          deck={deck}
          images={images}
          presenterId={agentId}
          onClose={() => setPresenting(false)}
        />
      )}

      {showPublish && deck && (
        <InstagramPublishModal
          deck={deck}
          images={images}
          aspect={aspect}
          clientId={task.clientId}
          accounts={socialAccounts.filter(a => a.platform === "instagram")}
          onClose={() => setShowPublish(false)}
        />
      )}
    </div>
  );
}

/* ====================== INSTAGRAM PUBLISH MODAL ====================== */
function InstagramPublishModal({ deck, images, aspect, clientId, accounts, onClose }: {
  deck: DeckSpec; images: DeckImages; aspect: DeckAspect; clientId?: string;
  accounts: { handle: string; accountId?: string; accessToken?: string }[];
  onClose: () => void;
}) {
  const settings = useSettings();
  const [accountIdx, setAccountIdx] = useState(0);
  const [caption, setCaption] = useState("");
  const [captionBusy, setCaptionBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const account = accounts[accountIdx];
  const slideUrls = Object.keys(images)
    .map(Number).sort((a, b) => a - b)
    .map(i => images[i]).filter((u): u is string => !!u);

  // auto-gera legenda na abertura usando IA + brand kit
  useEffect(() => {
    if (caption) return;
    autoCaption();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoCaption = async () => {
    const client = clientId ? CLIENTS.find(c => c.id === clientId) : undefined;
    const eff = client ? effectiveBrand(client, useClientBrand.getState().overrides[clientId!]) : undefined;
    if (!settings.anthropicKey) {
      // fallback simples
      const tags = client?.tags.slice(0, 6).map(t => "#" + t.replace(/[^a-z0-9]/gi, "")).join(" ") ?? "#agenciavpc";
      setCaption(`${deck.title}\n\n${deck.subtitle ?? ""}\n\n.\n.\n.\nSalva esse post.\n\n${tags} #marketing`);
      return;
    }
    setCaptionBusy(true);
    try {
      const text = await generateIGCaptionAI({
        apiKey: settings.anthropicKey,
        model: settings.model,
        deckTitle: deck.title,
        deckSubtitle: deck.subtitle,
        slides: deck.slides,
        brandHandle: account?.handle ?? `@${client?.id ?? "agencia"}`,
        brandName: client?.name ?? "VPC",
        voiceTone: eff?.voiceTone,
        audience: eff?.audience ?? client?.audience ?? "público amplo",
        clientTagsCsv: eff?.tagsCsv ?? client?.tags.join(", ")
      });
      setCaption(text);
    } catch (e) {
      const tags = client?.tags.slice(0, 6).map(t => "#" + t.replace(/[^a-z0-9]/gi, "")).join(" ") ?? "#agenciavpc";
      setCaption(`${deck.title}\n\n${deck.subtitle ?? ""}\n\n.\n.\n.\nSalva esse post.\n\n${tags} #marketing`);
    } finally { setCaptionBusy(false); }
  };

  const append = (s: string) => setLog(l => [...l, s]);

  const isCarousel = slideUrls.length >= 2;
  const aspectOK = aspect === "4:5" || aspect === "1:1";

  const publish = async () => {
    if (!account?.accessToken || !account?.accountId) {
      alert("Conta sem token. Configure em Social → Credenciais.");
      return;
    }
    if (slideUrls.length === 0) { alert("Sem imagens nos slides."); return; }
    if (!aspectOK) {
      if (!confirm(`Aspect ${aspect} não é ideal pro Instagram (use 4:5 ou 1:1). Continuar?`)) return;
    }

    setBusy(true);
    setLog(["▸ iniciando publicação no Instagram…"]);
    try {
      // 1. Upload das imagens pro API → URLs públicas
      append(`▸ subindo ${slideUrls.length} imagens pro storage…`);
      const apiBase = getApiBase();
      const authToken = getApiToken();
      if (!apiBase || !authToken) {
        throw new Error("Configure Cloud Sync API em ⚙️ primeiro (API base + token).");
      }
      const publicUrls: string[] = [];
      for (let i = 0; i < slideUrls.length; i++) {
        try {
          const url = await uploadDataUrl(apiBase, authToken, slideUrls[i], `slide-${i+1}.png`);
          publicUrls.push(url);
          append(`  ✓ slide ${i+1} → ${url.slice(-30)}`);
        } catch (e) {
          append(`  ✗ slide ${i+1} falhou: ${(e as Error).message.slice(0, 80)}`);
          throw e;
        }
      }

      // 2. Publica
      append(`▸ publicando ${isCarousel ? "carrossel" : "imagem"} no @${account.handle}…`);
      const igAccount = { accountId: account.accountId, accessToken: account.accessToken };
      const postId = isCarousel
        ? await publishCarousel(igAccount, publicUrls, caption)
        : await publishSingleImage(igAccount, publicUrls[0], caption);
      append(`✓ POSTADO! ID: ${postId}`);
      append(`→ veja em https://instagram.com/${account.handle.replace("@", "")}`);
      // marca o entry na biblioteca como publicado
      const lib = useLibrary.getState();
      const last = lib.entries.find(e => e.deck.title === deck.title);
      if (last) lib.update(last.id, { publishedToIG: true, publishedAt: Date.now(), igPostId: postId });
    } catch (e) {
      append(`✗ falha: ${(e as Error).message}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-[6px_6px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-3 flex items-center justify-between"
                style={{ background: "#E1306C", color: "white" }}>
          <div>
            <div className="font-mono text-xs opacity-80">PUBLICAR-IG</div>
            <h2 className="font-display font-black uppercase text-2xl leading-none">📤 Instagram</h2>
          </div>
          <button onClick={onClose} className="btn bg-bone text-ink border-bone">×</button>
        </header>

        <div className="p-5 space-y-3">
          {accounts.length > 1 && (
            <div>
              <div className="tag text-graydk mb-1">conta</div>
              <select value={accountIdx} onChange={(e) => setAccountIdx(Number(e.target.value))}
                className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
                {accounts.map((a, i) => (
                  <option key={i} value={i}>{a.handle}</option>
                ))}
              </select>
            </div>
          )}
          <div className="border-2 border-ink p-3 bg-bone">
            <div className="tag text-graydk mb-1">post type</div>
            <div className="font-display font-bold">
              {isCarousel ? `🎠 Carrossel · ${slideUrls.length} imagens` : "🖼️ Single image"}
            </div>
            <div className="tag text-graydk mt-2">aspect</div>
            <div className="font-mono text-[13px]">
              {aspect} {aspectOK ? "✓ ideal pro feed" : "⚠ use 4:5 ou 1:1"}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="tag text-graydk">legenda (max 2200 chars)</div>
              <button onClick={autoCaption} disabled={captionBusy}
                className="tag underline disabled:opacity-50">
                {captionBusy ? "🪄 escrevendo…" : "🪄 regerar com IA"}
              </button>
            </div>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)}
              rows={8}
              placeholder={captionBusy ? "IA escrevendo legenda baseada no carrossel + brand kit…" : ""}
              className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]" />
            <div className="tag text-graydk text-right">{caption.length} / 2200</div>
          </div>

          <button onClick={publish} disabled={busy}
            className="w-full py-3 text-base btn disabled:opacity-50"
            style={{ background: "#E1306C", color: "white", borderColor: "#E1306C" }}>
            {busy ? "publicando…" : "📤 PUBLICAR AGORA"}
          </button>

          {log.length > 0 && (
            <pre className="bg-ink/95 text-bone font-mono text-[11px] p-2 max-h-48 overflow-y-auto whitespace-pre-wrap">
{log.join("\n")}
            </pre>
          )}

          <p className="font-crimson italic text-paper text-[11px]">
            ⚠ As imagens são subidas pro nosso storage (api.agencia.cchub.com.br/uploads) e ficam públicas
            (Instagram exige URL pública). Após publicar, os arquivos continuam no storage.
          </p>
        </div>
      </div>
    </div>
  );
}

function slideToMarkdown(s: SlideSpec, i: number): string {
  const n = String(i + 1).padStart(2, "0");
  switch (s.layout) {
    case "cover":
      return `## ${n} · 🎬 CAPA\n# ${s.title}\n${s.subtitle ? `_${s.subtitle}_` : ""}`;
    case "section":
      return `## ${n} · ░░ SEÇÃO ░░\n# ${s.title}`;
    case "metric":
      return `## ${n} · 📊 ${s.title}\n# **${s.metric?.value ?? "—"}**\n_${s.metric?.caption ?? ""}_`;
    case "quote":
      return `## ${n} · 💬\n> "${s.quote?.text ?? ""}"\n> — **${s.quote?.author ?? ""}**`;
    case "closing":
      return `## ${n} · 🏁 ${s.title}\n${(s.bullets ?? []).map((b, j) => `${j+1}. ${b}`).join("\n")}`;
    default:
      return `## ${n} · ${s.title}\n${s.body ?? ""}\n${(s.bullets ?? []).map(b => `- ${b}`).join("\n")}`;
  }
}

function DeckPreview({ deck, images, aspect }: { deck: DeckSpec; images: DeckImages; aspect: DeckAspect }) {
  const [zoomIdx, setZoomIdx] = useState<number | null>(null);
  return (
    <div>
      <div className="tag text-graydk mb-2">preview · {deck.slides.length} slides · {aspect} · clique pra zoom</div>
      <div className={`grid gap-2 ${aspect === "9:16" || aspect === "4:5"
        ? "grid-cols-3 md:grid-cols-5"
        : "grid-cols-2 md:grid-cols-3"}`}>
        {deck.slides.map((s, i) => (
          <SlidePreview key={i} idx={i} slide={s} img={images[i] ?? null}
                        aspect={aspect}
                        primary={deck.brandPrimary} secondary={deck.brandSecondary}
                        accent={deck.brandAccent}
                        onZoom={() => setZoomIdx(i)} />
        ))}
      </div>

      {zoomIdx !== null && (
        <SlideZoom
          slide={deck.slides[zoomIdx]}
          img={images[zoomIdx] ?? null}
          idx={zoomIdx}
          total={deck.slides.length}
          aspect={aspect}
          primary={deck.brandPrimary} secondary={deck.brandSecondary} accent={deck.brandAccent}
          onPrev={() => setZoomIdx(z => z! > 0 ? z! - 1 : z)}
          onNext={() => setZoomIdx(z => z! < deck.slides.length - 1 ? z! + 1 : z)}
          onClose={() => setZoomIdx(null)}
        />
      )}
    </div>
  );
}

function SlideZoom({ slide, img, idx, total, aspect, primary, secondary, accent, onPrev, onNext, onClose }:
  { slide: SlideSpec; img: string | null; idx: number; total: number; aspect: DeckAspect;
    primary?: string; secondary?: string; accent?: string;
    onPrev: () => void; onNext: () => void; onClose: () => void }) {

  // navegação por teclado
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  const aspectClass =
    aspect === "16:9" ? "aspect-video"
    : aspect === "9:16" ? "aspect-[9/16]"
    : aspect === "1:1" ? "aspect-square"
    : "aspect-[4/5]";

  const downloadSlide = () => {
    if (!img) { alert("Slide sem imagem gerada."); return; }
    const a = document.createElement("a");
    a.href = img;
    a.download = `slide-${String(idx + 1).padStart(2, "0")}.png`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-ink/95 flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-bone z-10">
        <div className="font-mono text-sm">
          SLIDE {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} · {slide.layout ?? "bullets"} · {aspect}
        </div>
        <div className="flex gap-2">
          {img && (
            <button onClick={(e) => { e.stopPropagation(); downloadSlide(); }}
              className="btn bg-bone text-ink border-bone">↓ baixar slide</button>
          )}
          <button onClick={onClose} className="btn bg-red text-bone border-red">× fechar (ESC)</button>
        </div>
      </div>

      <button onClick={(e) => { e.stopPropagation(); onPrev(); }}
        disabled={idx === 0}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-bone text-ink font-display font-black text-2xl border-2 border-bone disabled:opacity-30">
        ←
      </button>

      <button onClick={(e) => { e.stopPropagation(); onNext(); }}
        disabled={idx === total - 1}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-bone text-ink font-display font-black text-2xl border-2 border-bone disabled:opacity-30">
        →
      </button>

      <div className="max-w-[90vw] max-h-[85vh]"
           onClick={(e) => e.stopPropagation()}>
        {img ? (
          <img src={img} alt=""
               className={`max-w-full max-h-[85vh] object-contain border-4 border-bone shadow-[8px_8px_0_0_rgba(0,0,0,0.5)]`} />
        ) : (
          <div className={`${aspectClass} bg-bone border-4 border-bone p-12 flex flex-col items-center justify-center w-[80vw] max-w-[1100px]`}
               style={{ background: accent ?? "#F2EEE6", color: primary ?? "#121214" }}>
            <div className="font-display font-black uppercase text-center" style={{ fontSize: "clamp(36px, 5vw, 72px)" }}>
              {slide.title}
            </div>
            {slide.subtitle && (
              <div className="font-serif italic mt-4 text-center" style={{ fontSize: "clamp(18px, 2vw, 28px)" }}>
                {slide.subtitle}
              </div>
            )}
            {slide.body && (
              <p className="font-serif italic mt-6 text-center max-w-2xl" style={{ fontSize: "clamp(16px, 1.6vw, 22px)" }}>{slide.body}</p>
            )}
            {slide.bullets && (
              <ul className="mt-6 space-y-2 text-center" style={{ fontSize: "clamp(16px, 1.8vw, 24px)" }}>
                {slide.bullets.map((b, j) => <li key={j}>▪ {b}</li>)}
              </ul>
            )}
            <div className="text-[10px] font-mono opacity-50 mt-8">(sem imagem gerada — preview textual)</div>
          </div>
        )}
      </div>
    </div>
  );
}

function SlidePreview({ idx, slide, img, aspect, primary, secondary, accent, onZoom }:
  { idx: number; slide: SlideSpec; img: string | null; aspect: DeckAspect;
    primary?: string; secondary?: string; accent?: string; onZoom?: () => void }) {

  // varia background mesmo dentro do mesmo layout pra evitar monotonia
  const variantSeed = idx;
  const isDark = slide.layout === "cover" || slide.layout === "section" || slide.layout === "quote";
  const isGold = slide.layout === "closing" || (slide.layout === "metric" && variantSeed % 3 === 0);
  // se for metric e não for gold, alterna entre accent e primary suave
  const isMetricAlt = slide.layout === "metric" && !isGold;
  const bg = isDark ? primary ?? "#121214"
           : isGold ? secondary ?? "#A8863C"
           : isMetricAlt && variantSeed % 2 === 1 ? primary ?? "#121214"
           : accent ?? "#F2EEE6";
  const fg = (bg === (primary ?? "#121214")) ? accent ?? "#F2EEE6"
           : isGold ? primary ?? "#121214"
           : primary ?? "#121214";

  const aspectClass =
    aspect === "16:9" ? "aspect-video"
    : aspect === "9:16" ? "aspect-[9/16]"
    : aspect === "1:1"  ? "aspect-square"
    : "aspect-[4/5]";

  const downloadSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!img) return;
    const a = document.createElement("a");
    a.href = img;
    a.download = `slide-${String(idx + 1).padStart(2, "0")}.png`;
    a.click();
  };

  return (
    <div className={`border-2 border-ink relative overflow-hidden ${aspectClass} group cursor-zoom-in`}
         style={{ background: bg, color: fg }}
         onClick={onZoom}>
      {/* Action overlay */}
      <div className="absolute top-1 right-1 z-20 flex gap-0.5 opacity-0 group-hover:opacity-100 transition">
        {img && (
          <button onClick={downloadSlide}
            title="baixar este slide"
            className="bg-ink text-bone w-6 h-6 flex items-center justify-center text-[10px] hover:bg-red">
            ↓
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onZoom?.(); }}
          title="ampliar"
          className="bg-ink text-bone w-6 h-6 flex items-center justify-center text-[10px] hover:bg-red">
          ⛶
        </button>
      </div>
      {img && (
        <img src={img} alt=""
             className={`absolute inset-0 w-full h-full object-cover
               ${slide.layout === "cover" ? "opacity-50" :
                 slide.layout === "image-left" ? "" :
                 slide.layout === "image-right" ? "" :
                 "opacity-20"}`}
             style={
               slide.layout === "image-left" ? { width: "50%", left: 0 } :
               slide.layout === "image-right" ? { width: "50%", right: 0, left: "auto" } :
               {}} />
      )}
      <div className="absolute top-1 left-1 text-[8px] font-mono opacity-70">
        {String(idx+1).padStart(2,"0")} · {slide.layout ?? "bullets"}
      </div>
      <div className="relative p-2 h-full flex flex-col justify-center"
           style={{
             paddingLeft: slide.layout === "image-left" ? "52%" : undefined,
             paddingRight: slide.layout === "image-right" ? "52%" : undefined
           }}>
        {slide.layout === "metric" ? (
          <>
            <div className="font-display font-black text-3xl leading-none"
                 style={{ color: secondary ?? "#A8863C" }}>
              {slide.metric?.value}
            </div>
            <div className="font-serif italic text-[10px] mt-1">{slide.metric?.caption}</div>
          </>
        ) : slide.layout === "quote" ? (
          <>
            <div className="font-serif italic text-[11px] leading-tight">"{slide.quote?.text}"</div>
            <div className="text-[8px] font-mono mt-1" style={{ color: secondary }}>— {slide.quote?.author}</div>
          </>
        ) : (
          <>
            <div className="font-display font-black text-[11px] leading-tight uppercase">
              {slide.title}
            </div>
            {slide.subtitle && (
              <div className="font-serif italic text-[8px] mt-0.5 opacity-80">{slide.subtitle}</div>
            )}
            {slide.body && (
              <div className="text-[8px] mt-1 line-clamp-3">{slide.body}</div>
            )}
            {slide.bullets && (
              <ul className="text-[8px] mt-1 space-y-0.5">
                {slide.bullets.slice(0, 4).map((b, j) => (
                  <li key={j} className="line-clamp-1">▪ {b}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
